/**
 * Phone number utilities for saved player search functionality
 */

/**
 * Normalizes a phone number by stripping all non-digit characters.
 * Examples:
 *   "(208) 555-1234" -> "2085551234"
 *   "208-555-1234" -> "2085551234"
 *   "+1 208 555 1234" -> "12085551234"
 * 
 * @param phone - The phone number string to normalize
 * @returns The digits-only normalized phone string
 */
export function normalizePhone(phone: string | undefined | null): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Minimum number of digits required for phone search to trigger.
 * This prevents overly broad matches.
 */
export const MIN_PHONE_SEARCH_LENGTH = 5;

/**
 * Checks if a normalized phone number matches a partial search query.
 * The query must have at least MIN_PHONE_SEARCH_LENGTH digits to match.
 * 
 * @param normalizedPhone - The full normalized phone number to search against
 * @param query - The search query (will be normalized internally)
 * @returns true if the phone starts with the normalized query and query is long enough
 */
export function matchesPartialPhone(normalizedPhone: string | undefined | null, query: string): boolean {
  if (!normalizedPhone) return false;
  
  const normalizedQuery = normalizePhone(query);
  
  // Require minimum digits for search
  if (normalizedQuery.length < MIN_PHONE_SEARCH_LENGTH) {
    return false;
  }
  
  return normalizedPhone.startsWith(normalizedQuery);
}

/**
 * Checks if a search query has enough digits to trigger phone search.
 * 
 * @param query - The search query
 * @returns true if the query has at least MIN_PHONE_SEARCH_LENGTH digits
 */
export function isValidPhoneSearchQuery(query: string): boolean {
  const normalized = normalizePhone(query);
  return normalized.length >= MIN_PHONE_SEARCH_LENGTH;
}

